<script setup lang="ts">
import MyStorySection from './MyStorySection.vue';
import PlannerDetailCoverImage from './PlannerDetailCoverImage.vue';
import PlannerDetailFooter from './PlannerDetailFooter.vue';
import PlannerDetailReviewSection from './PlannerDetailReviewSection/PlannerDetailReviewSection.vue';
import PlannerDetailSpecialtySection from './PlannerDetailSpecialtySection/PlannerDetailSpecialtySection.vue';
import PlannerProfile from './PlannerProfile.vue';
import PlannerSeminarSection from './PlannerSeminarSection/PlannerSeminarSection.vue';
import SectionTitle from './SectionTitle.vue';
import ReadmoreBlock from 'theme/components/molecules/ReadmoreBlock.vue';
import UserInputText from 'theme/components/molecules/UserInputText.vue';
import type { FragmentType } from '~/gql';
import { graphql } from '~/gql';

const props = defineProps<{
  planner: FragmentType<typeof PlannerDetailTemplateFragment>;
}>();

const PlannerDetailTemplateFragment = graphql(`
  fragment PlannerDetailTemplate on Planner {
    id
    name
    cover
    myStoryUrl
    comment
    review {
      count
      ...PlannerDetailReviewSection
    }
    ...PlannerProfile
    ...PlannerDetailSpecialtySection
    ...PlannerSeminarSection
  }
`);

const planner = computed(() =>
  getFragment(PlannerDetailTemplateFragment, props.planner)
);
</script>

<template>
  <div :class="$style.wrapper">
    <PlannerDetailCoverImage
      v-if="planner.cover != null"
      :class="[$style.wrapper__cover, $style.cover]"
      :img-src="planner.cover"
      :name="planner.name"
    />
    <div :class="[$style.wrapper__content, $style.content]">
      <PlannerProfile :class="$style.profile" :planner="planner" />
      <MyStorySection
        v-if="planner.myStoryUrl != null"
        :my-story-url="planner.myStoryUrl"
        :name="planner.name"
        :class="$style.mystory"
      />
      <section :class="$style.comment">
        <SectionTitle title="自己紹介" />
        <ReadmoreBlock :class="$style.comment__body" :collapse-lines="5">
          <UserInputText :text="planner.comment" is-break-line />
        </ReadmoreBlock>
      </section>
      <PlannerDetailSpecialtySection
        :class="$style.specialties"
        :planner="planner"
      />
      <PlannerDetailReviewSection
        v-if="planner.review.count !== 0"
        id="reviews"
        :class="$style.reviews"
        :planner-id="planner.id"
        :planner-review="planner.review"
      />
      <PlannerSeminarSection :class="$style.seminars" :planner="planner" />
    </div>
    <footer :class="[$style.wrapper__footer, $style.footer]">
      <PlannerDetailFooter :planner-id="planner.id" />
    </footer>
  </div>
</template>

<style lang="scss" module>
.wrapper {
  display: flex;
  flex-direction: column;

  &__content {
    flex: 1 0 0;
    margin: 0 auto;
    width: 100%;
    max-width: 720px;
    padding: 16px;
  }

  &__footer {
    flex: 0 0 auto;
  }
}

.cover {
  margin: 0 auto;
  max-width: 720px;
  width: 100%;
}

.profile {
  margin-top: 16px;
}

.mystory {
  margin-top: 24px;
}

.comment {
  margin-top: 22px;
  white-space: pre-line;

  &__body {
    margin-top: 8px;
  }
}

.specialties {
  margin-top: 52px;
}

.reviews {
  margin-top: 32px;
}

.seminars {
  margin-top: 32px;
}

.footer {
  position: sticky;
  bottom: 0;
}
</style>
